import {useEffect, useRef, useState} from "react";

const DEFAULT_TTL = 30000;

// Last time each key was fetched. Module state rather than the store: it is
// about the network, not about what the app is showing, and a browser refresh
// should always fetch again.
const fetchedAt = {};

export const markStale = (...keys) => {
  keys.forEach(key => delete fetchedAt[key]);
};

export const dataKeys = {
  SESSIONS: 'sessions',
  BOOKMARKED_SESSIONS: 'bookmarkedSessions',
  SURVEYS: 'surveys',
  SURVEY: 'survey',
  SESSION: 'session',
  FEEDS: 'feeds',
  COMMUNITY: 'community',
  MENTORSHIP: 'mentorship',
  MENTORSHIP_SESSIONS: 'mentorshipSessions',
  MENTORSHIP_CARDS: 'mentorshipCards',
  EVENTS: 'events',
};

export const dataKeyFor = (name, id) => `${name}:${id}`;

// Fetches on mount unless the same key was fetched in the last `ttl`, so moving
// between the hub, the survey and mentorship stops rebuilding each tab from
// scratch. Nothing is cleared first, so the page keeps what it has while a
// refetch fills in behind it. Returns false until there is something to show.
export default function useFreshData(key, load, {ttl = DEFAULT_TTL, enabled = true} = {}) {
  const [settled, setSettled] = useState(false);
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    if (!enabled || !key) return;

    if (Date.now() - (fetchedAt[key] || 0) < ttl) {
      setSettled(true);
      return;
    }

    // Marked before the request so two pages mounting together make one call,
    // and cleared again if it fails — a failure must not leave the page empty
    // with the key looking satisfied.
    fetchedAt[key] = Date.now();
    Promise.resolve(latest.current())
      .catch(() => markStale(key))
      .finally(() => setSettled(true));
  }, [key, enabled, ttl]);

  return settled;
}
